import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// html2canvas miscalculates text layout when the captured node sits inside a
// CSS `transform: scale()` wrapper (words stack/overlap). Neutralize any
// transformed ancestors during capture, then restore them.
async function captureCanvas(el, opts = {}) {
  const touched = [];
  let p = el.parentElement;
  while (p) {
    const t = getComputedStyle(p).transform;
    if (t && t !== "none") {
      touched.push([p, p.style.transform]);
      p.style.transform = "none";
    }
    p = p.parentElement;
  }
  try {
    return await html2canvas(el, { scale: 2, backgroundColor: "#ffffff", useCORS: true, ...opts });
  } finally {
    touched.forEach(([node, prev]) => { node.style.transform = prev; });
  }
}

export async function downloadImage(elementId, filename) {
  const el = document.getElementById(elementId);
  if (!el) return;
  const canvas = await captureCanvas(el);
  const link = document.createElement("a");
  link.href = canvas.toDataURL("image/jpeg", 0.95);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function shareImage(elementId, filename, { phone, caption } = {}) {
  const el = document.getElementById(elementId);
  if (!el) return "error";
  const canvas = await captureCanvas(el);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.95));
  if (!blob) return "error";
  const file = new File([blob], filename, { type: "image/jpeg" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], text: caption || "" });
      return "shared";
    } catch (e) {
      if (e && e.name === "AbortError") return "cancelled";
    }
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  const digits = (phone || "").replace(/\D/g, "");
  const text = encodeURIComponent(caption || "");
  const wa = digits ? `https://wa.me/${digits}?text=${text}` : `https://wa.me/?text=${text}`;
  window.open(wa, "_blank");
  return "fallback";
}

export async function downloadPdf(elementId, filename, orientation = "p") {
  const el = document.getElementById(elementId);
  if (!el) return;
  const canvas = await captureCanvas(el);
  const img = canvas.toDataURL("image/png");
  const pdf = new jsPDF(orientation, "mm", "a4");
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  const imgH = (canvas.height * pw) / canvas.width;
  let heightLeft = imgH;
  let position = 0;
  pdf.addImage(img, "PNG", 0, position, pw, imgH);
  heightLeft -= ph;
  while (heightLeft > 0) {
    position -= ph;
    pdf.addPage();
    pdf.addImage(img, "PNG", 0, position, pw, imgH);
    heightLeft -= ph;
  }
  pdf.save(filename);
}


// Print a specific element reliably by cloning it (with the page's styles)
// into a new window. Opening a real window (vs an off-screen iframe) avoids
// blank-print issues some Chrome builds have with hidden iframes, and also
// sidesteps Radix Dialog transform/overflow clipping.
export function printElement(elementId, { landscape = false, fullBleed = false, paper = null } = {}) {
  const el = document.getElementById(elementId);
  if (!el) return;

  let head = "";
  document.querySelectorAll('link[rel="stylesheet"], style').forEach((n) => { head += n.outerHTML; });
  const margin = fullBleed ? "0" : "10mm";
  const sizeCss = paper ? paper.css : `A4 ${landscape ? "landscape" : "portrait"}`;
  let pageW, pageH;
  if (paper) {
    const mpx = fullBleed ? 0 : 38;
    pageW = paper.wpx - mpx * 2;
    pageH = paper.hpx - mpx * 2;
  } else {
    pageW = fullBleed ? (landscape ? 1123 : 794) : (landscape ? 1047 : 718);
    pageH = fullBleed ? (landscape ? 794 : 1123) : (landscape ? 718 : 1047);
  }
  const pageCss = `<style>@page{size:${sizeCss};margin:${margin};} html,body{margin:0;padding:0;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact;} #${elementId}{transform-origin:top left;}</style>`;
  const html = `<!doctype html><html lang="id"><head><meta charset="utf-8"><title>Cetak</title>${head}${pageCss}</head><body>${el.outerHTML}</body></html>`;

  const runPrint = async (win, doc, cleanup) => {
    try {
      await new Promise((r) => setTimeout(r, 500));
      const imgs = Array.from(doc.images || []);
      await Promise.race([
        Promise.all(imgs.map((img) => (img.complete ? null : new Promise((r) => { img.onload = r; img.onerror = r; })))),
        new Promise((r) => setTimeout(r, 3000)),
      ]);
      const t = doc.getElementById(elementId);
      if (t) {
        const w = t.scrollWidth || t.offsetWidth;
        const h = t.scrollHeight || t.offsetHeight;
        const scale = Math.min(1, pageW / w, pageH / h);
        if (scale < 1) t.style.transform = `scale(${scale})`;
      }
    } catch (e) { /* noop */ }
    win.focus();
    win.print();
    cleanup();
  };

  const win = window.open("", "_blank", "width=1000,height=700");
  if (win) {
    win.document.open();
    win.document.write(html);
    win.document.close();
    runPrint(win, win.document, () => setTimeout(() => { try { win.close(); } catch (e) { /* noop */ } }, 400));
    return;
  }

  // Fallback: popup blocked -> use an off-screen iframe.
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;left:-10000px;top:0;border:0;";
  iframe.style.width = (paper ? paper.wpx : (landscape ? 1123 : 794)) + "px";
  iframe.style.height = (paper ? paper.hpx : (landscape ? 794 : 1123)) + "px";
  document.body.appendChild(iframe);
  const idoc = iframe.contentWindow.document;
  idoc.open();
  idoc.write(html);
  idoc.close();
  iframe.onload = () => runPrint(iframe.contentWindow, idoc, () => setTimeout(() => iframe.remove(), 1500));
}
